import React from 'react'
import { makeStyles, styled, Input } from '@material-ui/core'
import Pagination from '@material-ui/lab/Pagination'

import StyledSVG from '../shared/StyledSVG'
import searchIcon from '../../assets/icons/search-icon.svg'
import removeButton from '../../assets/icons/red-circle-remove.svg'
import emptyAvatar from '../../assets/icons/empty-avatar-icon.svg'

const ComponentWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  height: '100%',
  width: '100%',
  gap: 8,
  '& p.label': {
    margin: 0,
    fontSize: 12,
    fontWeight: 600
  }
}))

const InputWrapper = styled('div')(({ theme, bgColor }) => ({
  width: '100%',
  borderRadius: 8,
  backgroundColor: bgColor || 'unset',
  minHeight: 40,
  border: '1px solid #D2D5D8',
  '& .input': {
    borderRadius: 'unset',
    border: 'none'
  }
}))

const UserPhoto = styled('img')(({ theme, bgImg }) => {
  return {
    minWidth: 32,
    height: 32,
    borderRadius: 8,
    backgroundSize: 'cover',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
})

const useStyles = makeStyles(theme => ({
  root: {},
  selected_items: {
    display: 'flex',
    columnGap: 8,
    rowGap: 12,
    flexWrap: 'wrap',
    marginTop: 8
  },
  selected_item: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: 8,
    borderRadius: 8,
    backgroundColor: '#D9E8FA',
    '& p': {
      margin: 0,
      fontSize: 14,
      fontWeight: 500
    },
    '& .remove_item': {
      cursor: 'pointer',
      '& path': {
        fill: '#222A33'
      }
    }
  },
  search_component: {
    width: '100%',
    height: 40,
    padding: '8px 12px',
    borderRadius: '8px',
    border: '1px solid #D2D5D8',
    '& input': {
      padding: 0
    }
  },
  options: {
    '& .item_name_wrapper': {
      height: 48,
      display: 'flex',
      alignItems: 'center',
      '&:hover': {
        backgroundColor: '#F5F6F7'
      },
      '& p': {
        paddingLeft: 8,
        margin: 0,
        cursor: 'pointer'
      }
    }
  },
  item_selected: {
    backgroundColor: '#E9FAD9 !important',
    '&:hover': {
      backgroundColor: '#E9FAD9 !important'
    }
  },
  pagination_wrapper: {
    width: '100%',
    '& .MuiPagination-root': {
      padding: '0px 24px 12px 0px'
    }
  },
  pagination: {
    float: 'right',
    paddingTop: '15px',
    [theme.breakpoints.down('426')]: {
      float: 'unset',
      '& .MuiPagination-ul': {
        flexWrap: 'nowrap'
      }
    }
  }
}))

const InputSelect = ({
  isMulti,
  wrapperClass,
  fetchItems,
  params,
  placeholder,
  label,
  setSelectedItems,
  selectedItems,
  bgColor,
  attributesFromItem
}) => {
  const classes = useStyles()
  const inputSelectRef = React.useRef(null)
  const [page, setPage] = React.useState(1)
  const [expandedSelect, setExpandedSelect] = React.useState(false)
  const [inputValue, setInputVale] = React.useState(null)
  const [items, setItems] = React.useState({
    items: [],
    isFetching: false,
    meta: {}
  })

  React.useEffect(
    () => {
      function handleClickOutside (event) {
        if (
          inputSelectRef.current &&
          !inputSelectRef.current.contains(event.target)
        ) {
          setExpandedSelect(false)
        }
      }
      if (expandedSelect) {
        document.addEventListener('mousedown', handleClickOutside)
      }
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    },
    [expandedSelect]
  )

  React.useEffect(
    () => {
      setExpandedSelect(!!inputValue?.length)
    },
    [inputValue]
  )

  const getItemsFunction = async (page, searchParam) => {
    if (searchParam) {
      await fetchItems({
        page,
        params: {
          ...params,
          'q[name_cont]': searchParam
        }
      }).then(res => {
        setItems(prev => ({
          items: res.data.data || res.data,
          isFetching: false,
          meta: res.data.meta || res.meta
        }))
      })
      return
    }
    setItems(prev => ({
      items: [],
      isFetching: false,
      meta: {}
    }))
  }

  const handleAddItem = item => {
    setSelectedItems(prev => {
      const previousItem = prev.find(prvitem => +prvitem.id === +item.id)
      let newState
      const settedAttributes = attributesFromItem
        ? attributesFromItem
            .map(attr => {
              return { [attr]: item?.attributes?.[attr] }
            })
            .reduce((acc, obj) => ({ ...acc, ...obj }), {})
        : { name: item?.attributes?.name }

      if (!previousItem) {
        newState = isMulti
          ? [...prev, { id: +item.id, ...settedAttributes }]
          : [{ id: +item.id, ...settedAttributes }]
        return newState
      }
      newState = prev.filter(u => +u.id !== +previousItem.id)
      return newState
    })
  }

  const handleSeachByName = event => {
    const inputValue = event.target.value
    setInputVale(inputValue)
    getItemsFunction(1, inputValue)
  }

  const handleRemoveItem = item => {
    setSelectedItems(prev => {
      const newState = prev.filter(i => +i.id !== +item.id)
      return newState
    })
  }

  const handleChangePage = (event, value) => {
    setPage(value)
    getItemsFunction(value, inputValue)
  }

  return (
    <div className={wrapperClass}>
      <ComponentWrapper>
        <p className='label'>{label}</p>
        <InputWrapper bgColor={bgColor}>
          <Input
            disableUnderline
            value={inputValue}
            className={`${classes.search_component} input`}
            placeholder={placeholder}
            onChange={e => handleSeachByName(e)}
            endAdornment={
              <StyledSVG
                style={{ marginRight: 4 }}
                src={searchIcon}
                width={18}
                height={18}
              />
            }
          />
          <div ref={inputSelectRef}>
            {expandedSelect ? (
              items.items.length > 0 ? (
                <div className={classes.options}>
                  {items.items.map(item => {
                    const { id, ...rest } = item
                    const itemParsedToJsonApiFormat = item.attributes
                      ? item
                      : { id, attributes: { ...rest } }
                    const isSelected = selectedItems.some(
                      u => u.id === itemParsedToJsonApiFormat.id
                    )
                    return (
                      <div
                        key={itemParsedToJsonApiFormat.id}
                        onClick={() => handleAddItem(itemParsedToJsonApiFormat)}
                        className={`${isSelected &&
                          classes.item_selected} item_name_wrapper`}
                      >
                        <p>{itemParsedToJsonApiFormat.attributes.name}</p>
                      </div>
                    )
                  })}
                  <div className={classes.pagination_wrapper}>
                    {items.meta &&
                      items.meta.page_count > 1 && (
                        <Pagination
                          count={items.meta.page_count}
                          className={classes.pagination}
                          color='primary'
                          page={page}
                          onChange={handleChangePage}
                        />
                      )}
                  </div>
                </div>
              ) : (
                <p>Nenhum resultado encontrado</p>
              )
            ) : (
              <></>
            )}
          </div>
        </InputWrapper>
      </ComponentWrapper>
      <div className={classes.selected_items}>
        {selectedItems.map(item => {
          const photo = item?.photo_url || item?.avatar_url || emptyAvatar
          return (
            <div key={item.id} className={classes.selected_item}>
              <UserPhoto src={photo} />
              <p>{item.name}</p>
              <StyledSVG
                className='remove_item'
                onClick={() => handleRemoveItem(item)}
                src={removeButton}
                width={20}
                height={20}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default InputSelect
